// deployment_class: customer_env_allowed
/**
 * CEO redesign — module data model (Increment 1: the five-module Overview).
 *
 * One template renders every module from this data (build-once → every module
 * inherits). This IS the live design (N76 · single live artifact); the earlier HTML
 * design references that got us here are archived on the pre-consolidation branch.
 * The catalog holds SHAPE only — see the MODULES docblock below.
 *
 * Increment 1 is the OVERVIEW surface only — apex + KPI strip + what-needs-you +
 * cards, with connected-nav on the stream cards. Drills, workflows, the project
 * view + Data Ticker land in later increments. Nothing here is styled clickable
 * unless it is wired (Novello-clean by construction).
 */
export type ModuleKey = 'executive' | 'board' | 'planning' | 'projects' | 'oversight';
export type Rag = 'g' | 'y' | 'r';

export interface Kpi {
  k: string;
  v: string;
  s: string; // may contain <span class="up|dn"> — trusted static markup
}
export interface Need {
  p: number;
  tone: '' | 'amber';
  t: string;
  d: string; // trusted static markup (<b>)
  ai: string;
}
export interface CardDef {
  type: 'stream' | 'priority' | 'dept';
  icon: string;
  name: string;
  rag: [Rag, string];
  nav?: ModuleKey; // stream cards connect to their module
  projKey?: string; // project cards open the full project view
  bars?: Array<[string, number, Rag | '', string]>;
  big?: [string, string];
  donut?: number;
  meta: string; // trusted static markup
  /**
   * Projects this object (e.g. an Oversight department) belongs to / supports.
   * Surfaced in the drill so every module conveys WHICH projects it touches
   * (bug_UI_1f — "how do I know what project this belongs to?").
   */
  projects?: string[];
}
/**
 * SHAPE — everything knowable with an empty database.
 *
 * This is what the catalog holds, and the type is the enforcement. A content
 * field cannot be written here because it does not exist here: `tsc` rejects it
 * at the keystroke, with no gate, no review and no runtime strip involved.
 *
 * That replaces `bareModule()`, which removed content fields from catalog
 * entries at render. It worked, but it was the wrong mechanism for an invariant
 * a type can state: it had to enumerate every content field, and it silently
 * stopped covering `title` when `title` was (mis)classified as shape — which is
 * exactly how "Riverdale Housing Authority" reached production islands. A
 * checker you must remember to update is a checker that eventually lies.
 */
export interface ModuleShape {
  key: ModuleKey;
  icon: string;
  label: string;
  eyebrow: string;
  tabs: string[];
  sec: string;
  secHint: string;
}

/** SHAPE + the content a supplier provides. What actually renders. */
export interface ModuleDef extends ModuleShape {
  title: string;
  apex: { html: string; ring?: number; ringLbl?: string };
  kpis: Kpi[];
  needs?: Need[];
  cards: CardDef[];
}

// Rail order = Jason's module numbering (Executive · Board · Planning · Projects · Oversight)
export const MODULE_ORDER: ModuleKey[] = ['executive', 'board', 'planning', 'projects', 'oversight'];

/**
 * ── THE CATALOG HOLDS SHAPE, NEVER CONTENT (E3 lock 2026-09-01) ────────────
 *
 * E3: *"There's no more demo data associated with our production. That was
 * demo. Now we're production."*
 *
 * What stays here is what is knowable with an EMPTY DATABASE: which modules
 * exist, their rail order, icons, eyebrows, which tabs each one has, and the
 * section headings. That is the lens.
 *
 * What left: the Riverdale figures — apex narratives, KPI values,
 * what-needs-you items, cards, workflows and tab panels. They were compiled
 * into the customer bundle, so every agency on every island carried another
 * agency's numbers in its JavaScript. Moved verbatim to
 * `test/support/riverdale-demo-content.ts`, where a fixture belongs.
 *
 * `title` is CONTENT, not shape, and that distinction is the live bug this
 * closes. `bareModule()` already stripped apex/kpis/needs/cards, so those never
 * rendered — but `title` slipped through it and the dashboard `<h1>` read
 * "Riverdale Housing Authority" on production islands. An agency's NAME is the
 * most customer-specific datum there is. It now comes from the session
 * (`useAuth().organizations`), which is the agency's own name.
 *
 * Two titles remain, and they are product vocabulary rather than data: every
 * PHA has a board of commissioners and a development pipeline. `executive` and
 * `planning` are blank because an agency's name and its plan's period are its
 * own.
 *
 * ── Adding to this file ────────────────────────────────────────────────────
 * If a value would differ between two customers, it does not belong here.
 * `bin/kathedral-demo-residue-audit.ts` enforces the demo-identity half of that
 * mechanically; the rest is this sentence.
 */
export const MODULES: Record<ModuleKey, ModuleShape> = {
  executive: {
    key: 'executive', icon: '◆', label: 'Executive',
    eyebrow: 'Executive · the hub',
    tabs: ['Overview', 'Workflow', 'Reports', 'Messaging'],
    sec: 'The agency, by stream', secHint: 'each card opens its module',
  },
  board: {
    key: 'board', icon: '⚖', label: 'Board',
    eyebrow: 'Board · governance',
    tabs: ['Overview', 'Resolutions', 'Projects', 'Meetings', 'Board Packages'],
    sec: 'Board packet', secHint: 'the board-package build',
  },
  planning: {
    key: 'planning', icon: '◷', label: 'Planning',
    eyebrow: 'Planning · the strategy',
    tabs: ['Overview', 'Workflow', 'Objectives', 'Reports'],
    sec: 'Strategic priorities', secHint: 'each opens its objectives',
  },
  projects: {
    key: 'projects', icon: '⌂', label: 'Projects',
    eyebrow: 'Projects · development & acquisition',
    tabs: ['Overview', 'Workflow', 'Events', 'Import Data'],
    sec: 'Pipeline', secHint: 'the development pipeline',
  },
  oversight: {
    key: 'oversight', icon: '◉', label: 'Oversight',
    eyebrow: 'Oversight · operations',
    tabs: ['Overview', 'Workflow', 'Departments', 'Resolutions', 'Compliance'],
    sec: 'Departments', secHint: 'each opens its initiatives',
  },
};

// ── Workflows (Increment 3) — a workflow is a templatized MULTI-STEP PROCESS
// (an ordered checklist with an owner per step), NOT a single task. Ported from
// the design reference. The process survives CEO turnover — institutional, not
// tribal. Kept separate from MODULES so the Overview stays lean.
export type WfState = 'done' | 'now' | 'todo';
export interface Workflow {
  name: string;
  due: string;
  steps: Array<[string, WfState, string]>; // [label, state, owner]
}
export const WORKFLOWS: Record<ModuleKey, Workflow[]> = {
  executive: [], board: [], planning: [], projects: [], oversight: [],
};

// ── Tailored module tabs (N76 · built-out domain layers). Each non-Overview/
// Workflow tab renders a content panel of rows; a row opens a drill (no dead ends).
export interface TabRow {
  title: string; meta?: string; rag?: [Rag | '', string]; right?: string; src?: 'yardi' | 'kathedral' | 'manual';
  /** Optional message-thread enrichment (ui_bug1) — a richer drawer when the
   *  thread is opened: participants, status, the last message, the linked deal
   *  (click-through). Plain data (no Drill import) — messageDrill() builds it. */
  thread?: {
    from: string; to: string; status: string; statusTone?: Rag | '';
    last: string; deal?: string; extra?: Array<{ k: string; v?: string }>;
  };
}
export interface TabPanel { lead?: string; rows: TabRow[]; }
export const TAB_PANELS: Record<ModuleKey, Record<string, TabPanel>> = {
  executive: {}, board: {}, planning: {}, projects: {}, oversight: {},
};
